import React, { useEffect, useMemo, useRef, useState } from 'react';
import { RunnerPageDescription } from '@Client/runner.directives/common/PageDescription';
import { STATE_MAPPINGS } from '@Shared.Angular/@types/stateMappings';
import { CreationHubHeader } from './CreationHubHeader';
import FlowModelType from '@Shared.Angular/@types/core/contracts/constants/flowModelType';
import {
  CreationHubFlowModelDetailsPanel,
  CreationHubFlowModelDetails,
  FlowModelDetailsPanelMode
} from './CreationHubFlowModelDetailsPanel';
import { TextButton } from '@Shared.Angular/flowingly.components/buttons/TextButton';
import { getService } from '@Client/runner.hooks/useService';
import { CountdownTimer } from '@Shared.Angular/flowingly.components/CountdownTimer/CountdownTimer';
import { RecordingInProgressOverlay } from '@Shared.Angular/flowingly.components/CountdownTimer/RecordingInProgressOverlay';
import {
  RecordingPhase,
  VIDEO_RECORDING_COMPLETE,
  VIDEO_RECORDING_PHASE_CHANGED
} from '@Client/runner.services/video.recording.service';
import { EmptyGuid, Guid } from '@Shared.Angular/@types/guid';
import { FlowModelGenerationFileService } from '@Shared.Angular/flowingly.services/flowModel.generationFile.service';

export enum FlowModelCreationType {
  FromScratch = 'FromScratch',
  FromText = 'FromText',
  FromImage = 'FromImage',
  FromCsv = 'FromCsv',
  FromVideo = 'FromVideo',
  FromAudio = 'FromAudio',
  FromRecording = 'FromRecording'
}

type FlowModelFromXPageProps = {
  flowModelType: FlowModelType;
  creationType: FlowModelCreationType;
};

export const FlowModelFromXPage = ({
  flowModelType,
  creationType
}: FlowModelFromXPageProps) => {
  const videoRecordingService = getService('videoRecordingService');
  const pubsubService = getService('pubsubService');
  const $state = getService('$state');
  const modelerApiService = getService('modelerApiService');
  const creationHubService = getService('creationHubService');
  const notificationService = getService('notificationService');
  const tempModelerUrlService = getService('tempModelerUrlService');

  const [flowModelDetails, setFlowModelDetails] =
    useState<CreationHubFlowModelDetails>({
      name: '',
      description: '',
      objective: '',
      background: '',
      processInput: '',
      processOutput: '',
      triggerInput: ''
    });
  const flowModelDetailsRef = useRef(flowModelDetails);
  flowModelDetailsRef.current = flowModelDetails;
  const [validationError, setValidationError] = useState<string>('');
  const [showNameExistsError, setShowNameExistsError] = useState(false);
  const [isRequestPending, setIsRequestPending] = useState(false);
  const [isUploadPending, setIsUploadPending] = useState(false);
  const [recordingPhase, setRecordingPhase] = useState<RecordingPhase>(
    RecordingPhase.Inactive
  );
  const [videoBlob, setVideoBlob] = useState<Blob | null>(null);
  const [generationFileId, setGenerationFileId] = useState<Guid | null>(null);

  const isRecordingType = creationType === FlowModelCreationType.FromRecording;
  const isUploadType =
    !isRecordingType && creationType !== FlowModelCreationType.FromScratch;
  const creationHubState =
    flowModelType === FlowModelType.ProcessMap
      ? STATE_MAPPINGS.creationHubProcessMap
      : STATE_MAPPINGS.creationHubWorkflow;

  useEffect(() => {
    if (!isRecordingType) return;

    const subscriberId = 'flowingly.runner.creation-hub.flow-model-from-x';
    pubsubService.subscribe<RecordingPhase>(
      VIDEO_RECORDING_PHASE_CHANGED,
      (_event, newPhase) => setRecordingPhase(newPhase),
      subscriberId
    );
    pubsubService.subscribe<Blob>(
      VIDEO_RECORDING_COMPLETE,
      (_event, blob) => setVideoBlob(blob),
      subscriberId
    );
    return () => {
      pubsubService.unsubscribeAll(subscriberId);
    };
  }, []);

  useEffect(() => {
    if (!videoBlob) return;
    uploadVideoAndCreateFlowModel();
  }, [videoBlob]);

  const downloadUrl = useMemo(
    () => (videoBlob ? URL.createObjectURL(videoBlob) : null),
    [videoBlob]
  );

  const videoFileName = useMemo(() => {
    const name = flowModelDetails?.name || 'recording';
    return videoBlob
      ? `${name.trim().replace(/[:.]/g, '-')}` +
          '.' +
          (videoBlob.type?.split('/')[1] || 'webm')
      : null;
  }, [videoBlob, flowModelDetails.name]);

  const uploadAcceptTypes = useMemo(() => {
    switch (creationType) {
      case FlowModelCreationType.FromText:
        return ['.txt', '.md'];
      case FlowModelCreationType.FromImage:
        return ['.png', '.jpg', '.jpeg', '.gif', '.webp'];
      case FlowModelCreationType.FromCsv:
        return ['.csv'];
      case FlowModelCreationType.FromVideo:
        return ['.mp4', '.mov', '.webm'];
      case FlowModelCreationType.FromAudio:
        return ['.mp3', '.wav', '.aac', '.flac', '.m4a'];
      default:
        return [];
    }
  }, [creationType]);

  const detailsPanelMode = useMemo(() => {
    if (creationType === FlowModelCreationType.FromScratch) {
      return FlowModelDetailsPanelMode.FROM_SCRATCH;
    }
    if (isRecordingType) {
      return FlowModelDetailsPanelMode.FROM_RECORDING;
    }
    return FlowModelDetailsPanelMode.FROM_UPLOAD;
  }, [creationType]);

  const setUpdatedDetails = (details: CreationHubFlowModelDetails) => {
    if (showNameExistsError) {
      setShowNameExistsError(false);
    }
    setFlowModelDetails(details);
  };

  const isFlowModelNameInUse = async (): Promise<boolean> => {
    setIsRequestPending(true);
    try {
      const nameExists = await modelerApiService.checkFlowModelNameExists(
        flowModelDetailsRef.current.name
      );
      setIsRequestPending(false);
      if (nameExists) {
        setShowNameExistsError(true);
        return true;
      }
      return nameExists;
    } catch (error) {
      notificationService.showErrorToast(
        'Failed to check flow model name. Please try again.'
      );
      setIsRequestPending(false);
      return true;
    }
  };

  const createFlowModelFromScratch = async () => {
    if (await isFlowModelNameInUse()) return;
    setIsRequestPending(true);
    try {
      const flowModelId = await creationHubService.createDraftFlowModel(
        flowModelType,
        flowModelDetailsRef.current,
        false
      );
      tempModelerUrlService.openModeler({ flowModelId });
      $state.go(creationHubState);
    } catch {
      notificationService.showErrorToast(
        'Failed to create flow model. Please try again.'
      );
    } finally {
      setIsRequestPending(false);
    }
  };

  const createFlowModelFromUpload = async () => {
    if (await isFlowModelNameInUse()) return;
    setIsRequestPending(true);
    try {
      const flowModelId = await creationHubService.createDraftFlowModel(
        flowModelType,
        flowModelDetailsRef.current,
        true
      );
      tempModelerUrlService.openModeler({
        flowModelId,
        generateWithAi: true,
        generationFileId: flowModelDetailsRef.current.generationFileId
      });
      $state.go(creationHubState);
    } catch {
      notificationService.showErrorToast(
        'Failed to create flow model. Please try again.'
      );
    } finally {
      setIsRequestPending(false);
    }
  };

  const startRecording = async () => {
    if (await isFlowModelNameInUse()) return;
    videoRecordingService.acquireMedia();
  };

  const uploadVideoAndCreateFlowModel = async () => {
    videoRecordingService.resetPhase();

    if (videoBlob.size > FlowModelGenerationFileService.maxVideoFileSizeBytes) {
      notificationService.showErrorToast(
        'Video file is too large. Please record a shorter video.'
      );
      setVideoBlob(null);
      setIsRequestPending(false);
      return;
    }

    const generationFileService = getService('flowModelGenerationFileService');
    const file = new File([videoBlob], videoFileName, {
      type: videoBlob.type || 'video/webm'
    });

    try {
      setIsRequestPending(true);
      let fileId = generationFileId;
      if (!generationFileId) {
        const uploadedFile = await generationFileService.uploadFile(
          file,
          EmptyGuid
        );
        if (!uploadedFile.id) {
          notificationService.showErrorToast(
            'Upload failed. Please try again.'
          );
          setIsRequestPending(false);
          return;
        }
        fileId = uploadedFile.id;
        setGenerationFileId(uploadedFile.id);
      }
      creationHubService
        .createDraftFlowModel(flowModelType, flowModelDetailsRef.current, true)
        .then((flowModelId) => {
          tempModelerUrlService.openModeler({
            flowModelId,
            generateWithAi: true,
            generationFileId: fileId
          });
          $state.go(creationHubState);
        })
        .catch(() => {
          notificationService.showErrorToast(
            'Failed to create flow model. Please try again.'
          );
          setIsRequestPending(false);
        });
    } catch (error) {
      notificationService.showErrorToast(
        error?.message ?? 'Upload failed. Please try again.'
      );
      setIsRequestPending(false);
    }
  };

  const isFormDisabled =
    isRequestPending ||
    (isRecordingType && recordingPhase !== RecordingPhase.Inactive);

  const state: {
    title: string;
    actionButtonText: string;
    actionButtonHandler?: () => void | Promise<void>;
  } = useMemo(() => {
    switch (creationType) {
      case FlowModelCreationType.FromScratch:
        return {
          title: 'Build from Scratch',
          actionButtonText: 'Create',
          actionButtonHandler: createFlowModelFromScratch
        };
      case FlowModelCreationType.FromRecording:
        return {
          title: 'Record with AI',
          actionButtonText: videoBlob ? 'Retry upload' : 'Record',
          actionButtonHandler: videoBlob
            ? uploadVideoAndCreateFlowModel
            : startRecording
        };
      default: {
        const uploadTypeLabel = creationType.replace('From', '');
        return {
          title: `Generate from ${uploadTypeLabel}`,
          actionButtonText: 'Create',
          actionButtonHandler: createFlowModelFromUpload
        };
      }
    }
  }, [creationType, videoBlob]);

  return (
    <div className="flow-model-from-x-page creation-hub-page">
      {isRecordingType && recordingPhase === RecordingPhase.Countdown && (
        <CountdownTimer
          title="Recording will start in"
          seconds={3}
          onCountdownFinished={() => videoRecordingService.startRecording()}
        />
      )}
      {isRecordingType && recordingPhase === RecordingPhase.Recording && (
        <RecordingInProgressOverlay
          onStop={() => videoRecordingService.stopRecording()}
        />
      )}
      <RunnerPageDescription
        showText={false}
        backLinkText="Back"
        backLinkState={creationHubState}
      />
      <div className="creation-hub-page__content">
        <CreationHubHeader
          title={state.title}
          description={
            flowModelType === FlowModelType.ProcessMap
              ? 'Fill in the details to create your process map.'
              : 'Fill in the details to create your workflow.'
          }
        />
        <div className="creation-hub-page__content__form">
          <CreationHubFlowModelDetailsPanel
            mode={detailsPanelMode}
            uploadAcceptTypes={isUploadType ? uploadAcceptTypes : undefined}
            details={flowModelDetails}
            onDetailsChange={setUpdatedDetails}
            isDisabled={isFormDisabled}
            showNameExistsError={showNameExistsError}
            onValidationChange={setValidationError}
            onUploadPendingChange={
              isUploadType ? setIsUploadPending : undefined
            }
          />
          {isRecordingType && videoBlob && (
            <p className="flow-model-from-x-page__content__download-link-container">
              <a
                href={downloadUrl}
                download={videoFileName}
                data-testid="record-video-download-link"
              >
                <i className="fa-light fa-download"></i> Download video
              </a>
            </p>
          )}
          <div className="creation-hub-page__content__form__actions">
            <TextButton
              text="Cancel"
              type="text-only"
              testId="flow-model-from-x-cancel-button"
              disabled={isFormDisabled}
              onClick={() => {
                $state.go(creationHubState);
              }}
            />
            <TextButton
              text={state.actionButtonText}
              type="primary"
              testId="flow-model-from-x-create-button"
              disabled={!!validationError || isFormDisabled || isUploadPending}
              onClick={state.actionButtonHandler}
            />
          </div>
        </div>
      </div>
    </div>
  );
};
